import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {MachineGroupService} from '@services/administration/machine-group/machine-group.service';
import {MachineGroup} from '@models/adminstration/machine-group/machine-group';
import {MachineGroupMenuItems} from '@constants/administration/machine-group/machine-group-menu-items';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {utob} from '@prototypes/global.functions';
import {takeUntil} from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {Subject} from 'rxjs';
import {USER_ROLE} from '@constants/user-role.constant';

@Component({
    selector: 'scitis-admin-machine-group-configuration',
    templateUrl: './machine-group-configuration.component.html',
    styleUrls: ['./machine-group-configuration.component.scss'],
    standalone: false
})
export class MachineGroupConfigurationComponent implements OnInit, OnDestroy {

  private CURRENT_PAGE_NAME = 'MachineGroupConfiguration';
  public menuItems = MachineGroupMenuItems;
  public show: string = 'machines';
  public machineGroup: MachineGroup | undefined;
  public searchTerm: string = '';
  public userRole: Role = new Role(new User());
  private destroy$: Subject<boolean> = new Subject<boolean>();

  public whiteListedMachineryGroups: null | number[] = null;

  constructor(
      private activatedRoute: ActivatedRoute,
      private machineGroupService: MachineGroupService,
      private translate: TranslateService,
      private loadingIndicator: LoadingIndicatorService,
      private router: Router,
      private notification: NotificationService,
      private firebaseAuthenticationService: FirebaseAuthenticationService
  ) {
    this.searchTerm = this.router.currentNavigation()?.extras.state?.search;
    this.getMachineGroupDetailsFromUrl();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private getLoggedUSerDetails(): void {
    this.firebaseAuthenticationService.getLoggedUserDetails()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (user: User) => {
        this.whiteListedMachineryGroups = (USER_ROLE.SUPERADMIN === user.role || !user.isWhiteListedUser()) ? null : user.getWhiteListedMachineryGroups(true);
      }, error: (error: HttpResponse<any>) => {
        console.error(`${this.CURRENT_PAGE_NAME}. Error getting current user details.`, JSON.stringify(error));
      }
    });
  }

  private getMachineGroupDetailsFromUrl(): void {
    const details = this.activatedRoute.snapshot.paramMap.get('details');

    if (details !== null && details !== undefined) {
      const {id, user} = JSON.parse(utob(details));
      this.userRole = new Role(user);

      this.machineGroupService.getMachineryGroupDetails(id).subscribe({
        next: (machineGroup) => {
          this.machineGroup = machineGroup;
          this.loadingIndicator.hide();
        },
        error: (error) => {
          console.log(error);
          const message = this.translate.instant('Error getting machinery group details.');
          this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    }
    this.loadingIndicator.hide();
  }

  ngOnInit(): void {
    this.loadingIndicator.show(this.translate.instant('Loading machinery group details'));
    this.getLoggedUSerDetails();
    this.activatedRoute.queryParams.subscribe(p => this.show = p.page ?? this.menuItems.MACHINES);
  }

  onMenuClick(value: MachineGroupMenuItems) {
    this.show = value;
  }

  onNavChange(event: any) {
    this.show = event.nextId;
    this.router.navigate([], { relativeTo: this.activatedRoute, queryParams: { page: this.show } });
  }
}
